/**
 * Detailpaneel van één draaidag — het rechterpaneel uit de Airtable-interface.
 *
 * Toont de projectgegevens en maakt het voorbereiding-cluster bewerkbaar:
 * quitclaims, callsheet, printjes, props, catering en bijzonderheden.
 *
 * De voorbereiding kent twee stappen (workflow bevestigd door Joris, 20 aug):
 * 1. De PL vult alles in en vinkt "Productie-vooruit ingevuld?" → status ORANJE,
 *    het sein voor de producer (Tess) dat er geregeld kan worden.
 * 2. De producer regelt alles en vinkt "Preproductie voorbereid" → status GROEN.
 * Wijzigingen gaan per veld direct naar Airtable (typecast), zodat de
 * bestaande automations gewoon blijven reageren.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PLANNINGSTATUS_STIJL } from './DraaidagenOverzicht';
import {
  Autocomplete,
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Drawer, FormControlLabel,
  IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import WijzigingslogKnop from '../../ui-components/WijzigingslogKnop';
import ScriptSectie from './ScriptSectie';
import KlantVereistenBlok from '../../ui-components/KlantVereistenBlok';
import { agendaOmschrijving } from './agendaOmschrijving';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { initialenUitTitel, plUitInitialen } from '../../utils/draaidagPl';

const FONT = '"Manrope", system-ui, sans-serif';

const QUITCLAIM_OPTIES = ['Ja, fysiek', 'Ja, digitaal', 'Nee'];
const CATERING_OPTIES = ['Ontbijt', 'Lunch', 'Diner', 'Snacks'];

const VOORBEREIDING_BANNER = {
  'Preproductie voorbereid': { bg: '#16a34a', tekst: 'Preproductie voorbereid' },
  'Geen preproductie nodig': { bg: '#16a34a', tekst: 'Geen preproductie nodig' },
  'Productie-vooruit ingevuld': { bg: '#f59e0b', tekst: 'Productie-vooruit ingevuld — producer kan gaan regelen' },
};

const Sectie = ({ titel, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '0.9rem', color: '#2d3335', mb: 1 }}>
      {titel}
    </Typography>
    {children}
  </Box>
);

// Omschrijving uit de Outlook-agenda (Joris 29 sep): alleen wat echt is ingevuld, lange tekst inklapbaar.
function AgendaOmschrijving({ tekst }) {
  const [open, setOpen] = useState(false);
  const schoon = agendaOmschrijving(tekst);
  if (!schoon) return null;
  const lang = schoon.split('\n').length > 6 || schoon.length > 400;
  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>Uit de agenda</Typography>
      <Typography component="div" sx={{
        fontSize: '0.85rem', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5, mt: 0.25,
        ...(lang && !open ? { maxHeight: '9em', overflow: 'hidden', maskImage: 'linear-gradient(#000 70%, transparent)' } : {}),
      }}>
        {schoon}
      </Typography>
      {lang && (
        <Button size="small" onClick={() => setOpen((v) => !v)} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: '0.78rem' }}>
          {open ? 'Minder tonen' : 'Alles tonen'}
        </Button>
      )}
    </Box>
  );
}

export default function DraaidagDetailDrawer({ draaidagId, open, onClose, onBijgewerkt, portalContainer }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);
  // Tekstvelden bufferen we lokaal en bewaren we on-blur — elke toetsaanslag
  // naar Airtable sturen is traag én spamt de record-historie.
  const [tekst, setTekst] = useState({});
  // Race-guards (Codex-review): het paneel kan snel wisselen van draaidag terwijl
  // een oude fetch of save nog onderweg is. Elke async-respons mag alleen landen
  // als (a) het paneel nog dezelfde draaidag toont en (b) er geen nieuwere
  // schrijfactie is gestart — anders kon een oude respons data van draaidag A
  // over draaidag B heen zetten en werd een edit naar het verkeerde record gestuurd.
  const actueelIdRef = useRef(null);
  const volgnummerRef = useRef(0);
  useEffect(() => { actueelIdRef.current = open ? draaidagId : null; }, [open, draaidagId]);
  // Ctrl/Cmd+Z = laatste vinkje/statusveld terugdraaien, zolang het paneel open is.
  useEffect(() => (open ? registreerOngedaanSneltoets() : undefined), [open]);

  const [herlaadTeller, setHerlaadTeller] = useState(0);
  const herlaad = () => setHerlaadTeller((t) => t + 1);

  // Alleen bij een ánder record maken we het paneel leeg. Een herlaad van hetzelfde record
  // (na PL/crew/project wijzigen) is stil: de inhoud blijft staan en wordt ververst —
  // eerst verdween het hele paneel achter een spinner (Joris, 15 sep).
  const geladenIdRef = useRef(null);
  useEffect(() => { if (!open) geladenIdRef.current = null; }, [open]);   // heropenen = vers laden (Codex #12)
  useEffect(() => {
    if (!open || !draaidagId) return;
    const mijnId = draaidagId;
    const zelfde = geladenIdRef.current === mijnId;
    if (!zelfde) { setD(null); setTekst({}); setLaden(true); }
    setFout(null);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${mijnId}`);
        const data = await r.json();
        if (actueelIdRef.current !== mijnId) return; // paneel toont inmiddels iets anders
        if (!r.ok) throw new Error(data.error || data.detail || `HTTP ${r.status}`);
        geladenIdRef.current = mijnId;
        // Stil herladen: taken uit de vorige staat houden, tekstvelden niet overschrijven
        // (anders verdwijnt wat iemand net aan het typen was).
        setD((prev) => (zelfde && prev ? { ...data.draaidag, taken: data.draaidag.taken ?? prev.taken } : data.draaidag));
        if (!zelfde) {
          setTekst({
            printjesNodig: data.draaidag.printjesNodig || '',
            props: data.draaidag.props || '',
            bijzonderheden: data.draaidag.bijzonderheden || '',
            quitclaimsHoeveel: data.draaidag.quitclaimsHoeveel ?? '',
            cateringAantal: data.draaidag.cateringAantal ?? '',
          });
        }
      } catch (e) { if (actueelIdRef.current === mijnId) setFout(e.message); }
      finally { if (actueelIdRef.current === mijnId) setLaden(false); }
    })();
  }, [open, draaidagId, herlaadTeller]);

  // Datums Europees (dag-maand-jaar) — Airtable levert YYYY-MM-DD.
  const euDatum = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(String(iso))
    ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : (iso || '—'));

  // 📱 De PL appen over een ontbrekende callsheet — WhatsApp-deeplink met
  // voorgevulde vraag; zonder bekend nummer opent WhatsApp met alleen de tekst.
  const [appBusy, setAppBusy] = useState(false);
  const appDePl = async () => {
    setAppBusy(true);
    try {
      const plNaam = String(d?.pl || '').split(',')[0].trim();
      let nummer = '';
      if (plNaam) {
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/personen/all`);
          const dta = await leesJson(r, 'Personen laden');
          const lijst = dta.personen || dta.persons || dta.records || [];
          const p2 = lijst.find((x) => {
            const f = x.fields || x;
            const naam = String(f['Volledige naam'] || f.Naam || f.full_name || '');
            return naam.toLowerCase().startsWith(plNaam.toLowerCase());
          });
          const f2 = p2?.fields || p2 || {};
          nummer = String(f2.Telefoonnummer || f2.phone || f2.Telefoon || '').replace(/[^\d+]/g, '');
          if (nummer.startsWith('06')) nummer = `31${nummer.slice(1)}`;
          nummer = nummer.replace(/^\+/, '');
        } catch { /* bewust stil: zonder nummer opent WhatsApp zonder ontvanger — kan ook */ }
      }
      const tekst = encodeURIComponent(
        `Hoi ${plNaam || 'PL'}, weet je al meer over de draaidag "${d?.titel || ''}" op ${euDatum(d?.startDatum)}? (tijden / callsheet)`);
      window.open(nummer ? `https://wa.me/${nummer}?text=${tekst}` : `https://wa.me/?text=${tekst}`, '_blank', 'noopener');
    } finally { setAppBusy(false); }
  };

  // PL (extra) koppelen — schrijft naar het Projectleider-veld van het project.
  const [plKiezer, setPlKiezer] = useState(false);
  const [crewKiezer, setCrewKiezer] = useState(false);
  // ➕ Kleine taak bij het gekoppelde project — assignee standaard de PL,
  // aanpasbaar in het schermpje (Joris, 1 sep).
  const [taakForm, setTaakForm] = useState(null); // { naam, uren, datum, assigneeId, busy }
  const openTaakForm = async () => {
    setTaakForm({ naam: '', uren: '', datum: (d?.startDatum || '').slice(0, 10), assigneeId: '', busy: false });
    if (!medewerkers.length) setCrewKiezer(true); // triggert de personen-load
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${actueelIdRef.current}/pl`);
      const dta = await leesJson(r, 'PL ophalen');
      const pl = (dta.pls || [])[0];
      if (pl?.id) setTaakForm((f) => (f ? { ...f, assigneeId: pl.id, assigneeNaam: pl.naam } : f));
    } catch (e) { meldFout(e, 'PL ophalen'); /* geen PL — gebruiker kiest zelf */ }
    setCrewKiezer(false);
  };
  const bewaarTaak = async () => {
    if (!taakForm?.naam.trim()) return;
    setTaakForm((f) => ({ ...f, busy: true }));
    try {
      const payload = {
        NaamTaak: taakForm.naam.trim(),
        PrioNr: 'Geen prio',
        ...(taakForm.uren ? { UrenWerk: Number(taakForm.uren) } : {}),
        ...(taakForm.datum ? { StartDateUitvoerder: taakForm.datum, EndDateUitvoerder: taakForm.datum } : {}),
        ...(d?.projectId ? { Project: [d.projectId] } : {}),
        ...(taakForm.assigneeId ? { Assignee: [taakForm.assigneeId] } : {}),
      };
      const r = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const dta = await r.json();
      if (!r.ok) throw new Error(dta.error || `HTTP ${r.status}`);
      toast.success(`Taak toegevoegd${d?.projectnummer ? ` bij project ${d.projectnummer}` : ''}`);
      setTaakForm(null);
      onBijgewerkt?.();
    } catch (e) {
      toast.error(`Taak toevoegen mislukt: ${e.message}`);
      setTaakForm((f) => (f ? { ...f, busy: false } : f));
    }
  };
  const [medewerkers, setMedewerkers] = useState([]);
  const [plBusy, setPlBusy] = useState(false);
  const [huidigePls, setHuidigePls] = useState(null); // [{id,naam}] van het project
  useEffect(() => {
    if (!plKiezer || !actueelIdRef.current) { setHuidigePls(null); return; }
    authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${actueelIdRef.current}/pl`)
      .then((r) => (r.ok ? r.json() : { pls: [] }))
      .then((dta) => setHuidigePls(dta.pls || []))
      .catch(() => setHuidigePls([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plKiezer]);
  useEffect(() => {
    if ((!plKiezer && !crewKiezer) || medewerkers.length) return;
    authFetch(`${BACKEND_URL}/Api/personen/all`)
      .then((r) => (r.ok ? r.json() : { personen: [] }))
      .then((dta) => {
        const lijst = dta.personen || dta.persons || dta.records || [];
        // Alleen actieve medewerkers/stagiairs, en ontdubbeld op naam — Airtable
        // bevat dubbele persoonsrecords (naomi@ vs Naomi@) en archiefversies,
        // waardoor dezelfde naam vijf keer in de keuzelijst stond (1 sep).
        const gezien = new Set();
        setMedewerkers(lijst
          .filter((x) => {
            const f = x.fields || {};
            const status = f.Status || x.status;
            const type = f.Type || x.type;
            return (!status || status === 'Active')
              && (!type || ['Medewerker', 'Stagiair'].includes(Array.isArray(type) ? type[0] : type));
          })
          .map((x) => ({ id: x.id, naam: x.fields?.['Volledige naam'] || x.full_name || x.name || '' }))
          .filter((x) => {
            if (!x.id || !x.naam) return false;
            const sleutel = x.naam.trim().toLowerCase();
            if (gezien.has(sleutel)) return false;
            gezien.add(sleutel);
            return true;
          })
          .sort((a, b) => a.naam.localeCompare(b.naam)));
      })
      .catch(() => {});
  }, [plKiezer, crewKiezer, medewerkers.length]);
  const koppelPl = async (persoon, actie = 'toevoegen') => {
    if (!persoon?.id) return;
    setPlBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${actueelIdRef.current}/pl`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personId: persoon.id, actie }),
      });
      const dta = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(dta.error || `HTTP ${r.status}`);
      let nieuweLijst = null;
      setHuidigePls((lijst) => {
        if (!Array.isArray(lijst)) return lijst;
        nieuweLijst = actie === 'verwijderen'
          ? lijst.filter((x) => x.id !== persoon.id)
          : (lijst.some((x) => x.id === persoon.id) ? lijst : [...lijst, { id: persoon.id, naam: persoon.naam }]);
        return nieuweLijst;
      });
      // Meteen in beeld: de PL-chip volgt de nieuwe lijst; de stille herlaad bevestigt het.
      if (nieuweLijst) setD((prev) => (prev ? { ...prev, pl: nieuweLijst.map((x) => x.naam).join(', ') } : prev));
      herlaad();
      onBijgewerkt?.();
    } catch (e) { setFout(`PL aanpassen mislukt: ${e.message}`); }
    finally { setPlBusy(false); }
  };

  // Project koppelen — én wijzigen als er al een gekoppeld project is (1 sep).
  const [projectWijzig, setProjectWijzig] = useState(false);
  const [projectZoek, setProjectZoek] = useState('');
  const [projectOpties, setProjectOpties] = useState([]);
  const projectZoekTimer = useRef(null);
  const zoekProjecten = (q) => {
    setProjectZoek(q);
    if (projectZoekTimer.current) clearTimeout(projectZoekTimer.current);
    if (!q || q.trim().length < 2) { setProjectOpties([]); return; }
    projectZoekTimer.current = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/projects?search=${encodeURIComponent(q.trim())}`);
        const dta = await leesJson(r, 'Projecten zoeken');
        setProjectOpties((dta.projects || []).slice(0, 12).map((p) => ({
          id: p.id,
          label: p.fields?.Projectnr_en_naam || p.fields?.Projectnaam || p.id,
        })));
      } catch (e) { meldFout(e, 'Projecten zoeken'); setProjectOpties([]); }
    }, 350);
  };

  const bewaar = useCallback(async (velden) => {
    const mijnId = draaidagId;
    const mijnVolgnummer = ++volgnummerRef.current;
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${mijnId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(velden),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || data.detail || `HTTP ${r.status}`);
      // Alleen de nieuwste respons voor het nog-getoonde record mag landen; de
      // PATCH-respons bevat geen taken, dus die houden we uit de vorige staat.
      if (actueelIdRef.current === mijnId && volgnummerRef.current === mijnVolgnummer) {
        setD((prev) => ({ ...data.draaidag, taken: prev?.taken ?? [] }));
      }
      onBijgewerkt?.();
      return true;
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); return false; }
    finally { if (volgnummerRef.current === mijnVolgnummer) setBezig(false); }
  }, [draaidagId, onBijgewerkt]);

  // Vinkjes en statusvelden: opslaan mét "Ongedaan maken" — de vorige waarde
  // (uit de getoonde staat, vóór de PATCH) wordt dan teruggezet.
  const bewaarMetOngedaan = async (velden, tekst = 'Opgeslagen') => {
    const vorige = Object.fromEntries(Object.entries(velden).map(([k, v]) => {
      if (typeof v === 'boolean') return [k, Boolean(d?.[k])];
      if (Array.isArray(v)) return [k, d?.[k] || []];
      return [k, d?.[k] ?? null];
    }));
    if (await bewaar(velden)) {
      biedOngedaanAan(tekst, async () => { if (!(await bewaar(vorige))) throw new Error('Terugzetten mislukt'); });
    }
  };

  const banner = d && (VOORBEREIDING_BANNER[d.voorbereidingsstatus]
    || { bg: '#dc2626', tekst: d.voorbereidingsstatus || 'Nog niks voorbereid' });

  const checkbox = (label, key) => (
    <FormControlLabel
      control={<Checkbox size="small" checked={Boolean(d?.[key])} disabled={bezig}
        onChange={(e) => bewaarMetOngedaan({ [key]: e.target.checked }, `${label}: ${e.target.checked ? 'aan' : 'uit'}`)} />}
      label={<Typography sx={{ fontSize: '0.85rem' }}>{label}</Typography>}
    />
  );

  const selectVeld = (value, key, opties, { multiple = false, placeholder = '—' } = {}) => (
    <Select
      size="small" fullWidth multiple={multiple} displayEmpty disabled={bezig}
      value={multiple ? (value || []) : (value || '')}
      onChange={(e) => bewaarMetOngedaan({ [key]: e.target.value }, 'Keuze opgeslagen')}
      renderValue={(v) => (multiple
        ? (v.length ? v.join(', ') : <em>{placeholder}</em>)
        : (v || <em>{placeholder}</em>))}
      sx={{ fontSize: '0.85rem', bgcolor: '#fff' }}
    >
      {!multiple && <MenuItem value=""><em>—</em></MenuItem>}
      {opties.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: '0.85rem' }}>{o}</MenuItem>)}
    </Select>
  );

  const tekstVeld = (key, { label, rows = 3, type = 'text' } = {}) => (
    <TextField
      size="small" fullWidth multiline={rows > 1} minRows={rows > 1 ? rows : undefined}
      type={type} label={label} disabled={bezig}
      value={tekst[key] ?? ''}
      onChange={(e) => setTekst((t) => ({ ...t, [key]: e.target.value }))}
      onBlur={() => {
        const huidig = type === 'number'
          ? (tekst[key] === '' ? null : Number(tekst[key]))
          : tekst[key];
        const origineel = d?.[key] ?? (type === 'number' ? null : '');
        if (huidig !== origineel) bewaar({ [key]: huidig });
      }}
      sx={{ bgcolor: '#fff' }}
    />
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      // In presentatie-/fullscreen-modus moet de portal BINNEN het
      // gefullscreende element renderen, anders is het paneel onzichtbaar.
      ModalProps={{ container: portalContainer || undefined }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0 } }}>
      <Box sx={{ p: 2.5, pb: 6 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '1.05rem', color: '#1f2937', flex: 1, lineHeight: 1.35 }}>
            {d?.titel || 'Draaidag'}
          </Typography>
          {draaidagId && <WijzigingslogKnop entityId={draaidagId} titel={d?.titel || 'Draaidag'} size={18} />}
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {laden && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={26} /></Box>}
        {fout && <Alert severity="error" sx={{ mt: 2 }}>{fout}</Alert>}

        {d && (
          <>
            {/* Verwijderde Outlook-afspraak: melding + bewuste verwijder-knop
                (zoals in Airtable) — bewust géén automatische opruiming, want de
                crew moet eerst ingelicht worden (Joris, 1 sep). */}
            {d.foutmelding && (
              <Alert severity="error" sx={{ mt: 1.5 }}
                action={
                  <Button color="error" size="small" variant="outlined" sx={{ textTransform: 'none', fontWeight: 700 }}
                    onClick={async () => {
                      if (await bevestig({ titel: 'Draaidag verwijderen?', tekst: 'Licht eerst de crew in. De draaidag wordt uit de planning verwijderd (verborgen).', knop: 'Verwijderen', gevaarlijk: true })) {
                        bewaar({ verbergen: true });
                        onClose();
                      }
                    }}>
                    🗑 Verwijderen
                  </Button>
                }>
                {d.foutmelding.includes('Outlook') ? d.foutmelding
                  : 'Deze draaidag is niet langer gekoppeld aan de Outlook-agenda. Mogelijk is de afspraak verwijderd.'}
              </Alert>
            )}

            {/* Voorbereidingsstatus-banner, zoals "Nog niks voorbereid" in Airtable */}
            <Box sx={{ mt: 1.5, mb: 2, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: banner.bg, color: '#fff', fontWeight: 800, fontFamily: FONT, fontSize: '0.85rem' }}>
              {banner.tekst}
            </Box>

            {(d.projectId || d.projectnummer) && (
              <KlantVereistenBlok projectId={d.projectId} nummer={d.projectnummer} variant="detail" sx={{ mt: 0, mb: 3 }} />
            )}

            <Sectie titel="Projectgegevens">
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 1.5, rowGap: 1.25, mb: 1.25 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>Startdatum</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{euDatum(d.startDatum)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>Einddatum</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{euDatum(d.eindDatum)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>Aantal dagen</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{d.dagen}</Typography>
                </Box>
                {Number(d.dagen) === 1 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, display: 'block' }}>Duur</Typography>
                    {/* Halve dag = 4 uur: de crew houdt dan capaciteit over voor
                        andere taken (Joris, 1 sep). */}
                    <Select size="small" variant="standard" disableUnderline
                      value={Number(d.duurUren) === 4 ? 4 : 8}
                      onChange={(e) => bewaar({ duurUren: Number(e.target.value) })}
                      sx={{ fontSize: '0.9rem', fontWeight: 700, '& .MuiSelect-select': { py: 0 } }}>
                      <MenuItem value={8}>Hele dag (8u)</MenuItem>
                      <MenuItem value={4}>Halve dag (4u)</MenuItem>
                    </Select>
                  </Box>
                )}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 0.25 }}>
                <Chip size="small" label={d.planningstatus || 'Planningstatus onbekend'}
                  sx={{ fontWeight: 700,
                    bgcolor: (PLANNINGSTATUS_STIJL[d.planningstatus] || {}).bg || '#e2e8f0',
                    color: (PLANNINGSTATUS_STIJL[d.planningstatus] || {}).fg || '#475569' }} />
                {d.pl && <Chip size="small" label={`PL: ${d.pl}`} sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }} />}
                {!d.pl && initialenUitTitel(d.origineleTitel) && (() => {
                  const p = plUitInitialen(initialenUitTitel(d.origineleTitel), medewerkers);
                  return (
                    <Tooltip title={p.zeker ? `Volgens de initialen in de agendatitel (${p.initialen}). Koppel de PL hieronder om het in Airtable vast te leggen.`
                      : `Initialen ${p.initialen} in de agendatitel${p.kandidaten?.length ? ` — kan zijn: ${p.kandidaten.join(', ')}` : ''}. Koppel de PL hieronder.`}>
                      <Chip size="small" label={`PL volgens titel: ${p.zeker ? p.voornaam : p.initialen}`}
                        sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontStyle: 'italic' }} />
                    </Tooltip>
                  );
                })()}
                {/* Geen project (nog geen offerte/opdracht) én geen PL: handmatige
                    PL op de draaidag zelf — Airtable-veld 'Projectleider (Handmatig)',
                    de PL-formule pakt hem vanzelf op (Joris, 1 sep). */}
                {!d.projectId && !d.projectnummer && !d.pl && (
                  <Autocomplete size="small" options={medewerkers} sx={{ minWidth: 200 }}
                    onOpen={() => setCrewKiezer(true)}
                    getOptionLabel={(o) => o?.naam || ''}
                    onChange={(_e, keuze) => keuze?.id && bewaar({ plHandmatigId: keuze.id })}
                    renderInput={(params) => (
                      <TextField {...params} variant="standard" placeholder="👤 PL toevoegen…"
                        inputProps={{ ...params.inputProps, style: { fontSize: '0.8rem' } }} />
                    )} />
                )}
                {d.overdracht && (
                  <Tooltip title={`${d.overdracht.van} heeft dit project overgedragen aan ${d.overdracht.naar}${d.overdracht.tot ? ` t/m ${euDatum(d.overdracht.tot)}` : ''} — klik voor de overdracht-pagina`}>
                    <Chip size="small" label={`🔁 Waargenomen door ${d.overdracht.naar}${d.overdracht.tot ? ` · t/m ${euDatum(d.overdracht.tot)}` : ''}`}
                      onClick={() => navigate('/overdracht')}
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, cursor: 'pointer' }} />
                  </Tooltip>
                )}
                {(d.projectId || d.projectnummer) && !plKiezer && (
                  d.pl ? (
                    <Tooltip title="Projectleider(s) aanpassen">
                      <IconButton size="small" onClick={() => setPlKiezer(true)} sx={{ p: 0.25, ml: -0.5 }}>
                        <EditOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8', '&:hover': { color: '#3730a3' } }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Chip size="small" label="+ PL koppelen" variant="outlined"
                      onClick={() => setPlKiezer(true)}
                      sx={{ fontWeight: 700, cursor: 'pointer', color: '#3730a3', borderColor: '#c7d2fe' }} />
                  )
                )}
              </Stack>
              {plKiezer && (
                <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, bgcolor: '#eef2ff', border: '1px solid #c7d2fe' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                    {huidigePls === null && <CircularProgress size={14} />}
                    {(huidigePls || []).map((p2) => (
                      <Chip key={p2.id} size="small" label={p2.naam} disabled={plBusy}
                        onDelete={() => koppelPl(p2, 'verwijderen')}
                        sx={{ bgcolor: '#fff', fontWeight: 700 }} />
                    ))}
                    {Array.isArray(huidigePls) && huidigePls.length === 0 && (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Nog geen PL op dit project.</Typography>
                    )}
                    <Autocomplete size="small" options={medewerkers.filter((mw) => !(huidigePls || []).some((x) => x.id === mw.id))}
                      sx={{ minWidth: 200, flex: 1 }} autoHighlight openOnFocus
                      loading={plBusy} getOptionLabel={(o) => o?.naam || ''}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={null} blurOnSelect clearOnBlur
                      onChange={(_e, p2) => { if (p2) koppelPl(p2, 'toevoegen'); }}
                      renderInput={(params) => (
                        <TextField {...params} autoFocus variant="standard" placeholder="+ projectleider toevoegen…"
                          onKeyDown={(e) => { if (e.key === 'Escape') setPlKiezer(false); }}
                          inputProps={{ ...params.inputProps, style: { fontSize: '0.8rem' } }} />
                      )} />
                    <Chip size="small" label="klaar" onClick={() => setPlKiezer(false)}
                      sx={{ fontWeight: 700, cursor: 'pointer' }} />
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                    Wijzigt de projectleider(s) van het gekoppelde project — kruisje = weghalen.
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 0.75 }}>
                {d.projectnummer && !projectWijzig && (
                  <>
                    <Chip size="small" icon={<OpenInNewIcon sx={{ fontSize: 14 }} />} label={`Project ${d.projectnummer}`}
                      onClick={() => d.projectId && navigate(`/projects/${d.projectId}`)}
                      // Kruisje = project loskoppelen (Joris, 15 sep); Ongedaan maken hangt hem weer aan.
                      onDelete={bezig ? undefined : () => bewaarMetOngedaan({ projectId: null }, `Project ${d.projectnummer} losgekoppeld`)}
                      deleteIcon={<Tooltip title="Project loskoppelen"><CloseIcon sx={{ fontSize: 14 }} /></Tooltip>}
                      sx={{ fontWeight: 700, cursor: 'pointer' }} />
                    <Tooltip title="Ander project koppelen">
                      <IconButton size="small" onClick={() => { setProjectWijzig(true); setProjectZoek(''); setProjectOpties([]); }} sx={{ p: 0.25 }}>
                        <EditOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {((!d.projectnummer && !d.projectId) || projectWijzig) && (
                  <Autocomplete size="small" options={projectOpties} filterOptions={(x) => x}
                    sx={{ minWidth: 260 }} noOptionsText="Typ om te zoeken (nummer of naam)"
                    getOptionLabel={(o) => o?.label || ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    inputValue={projectZoek}
                    onInputChange={(_e, val, reason) => { if (reason === 'input') zoekProjecten(val); }}
                    onChange={(_e, keuze) => { if (keuze?.id) { bewaar({ projectId: keuze.id }); setProjectWijzig(false); } }}
                    renderInput={(params) => (
                      <TextField {...params} variant="standard"
                        placeholder={projectWijzig ? '🔗 ander project kiezen…' : '🔗 project koppelen…'}
                        inputProps={{ ...params.inputProps, style: { fontSize: '0.8rem' } }} />
                    )} />
                )}
                {projectWijzig && (
                  <IconButton size="small" onClick={() => setProjectWijzig(false)} sx={{ p: 0.25 }} title="Annuleren">
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
                {d.isBuitenland && <Chip size="small" label="🌍 Buitenland" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />}
              </Stack>
              {d.locatie && (
                <Typography sx={{ fontSize: '0.85rem', color: '#475569', mt: 1 }}>📍 {d.locatie}</Typography>
              )}
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, mr: 0.5, minWidth: 34 }}>Crew</Typography>
                {/* Bewerkbaar (Joris, 1 sep): kruisje = uit de crew, + = toevoegen.
                    Namen en ids lopen in Airtable-lookup-volgorde gelijk op. */}
                {d.crew.map((c, i) => (
                  <Chip key={`${c}-${i}`} size="small" label={c} sx={{ bgcolor: '#f1f5f9' }}
                    onDelete={d.crewIds?.length === d.crew.length ? () => {
                      const nieuw = d.crewIds.filter((_x, j) => j !== i);
                      bewaar({ crew: nieuw });
                    } : undefined} />
                ))}
                {!crewKiezer ? (
                  <Chip size="small" label="+ crew" clickable onClick={() => setCrewKiezer(true)}
                    sx={{ bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }} />
                ) : (
                  <Autocomplete size="small" options={medewerkers.filter((m) => !(d.crew || []).includes(m.naam))}
                    sx={{ minWidth: 220 }} autoHighlight openOnFocus
                    getOptionLabel={(o) => o?.naam || ''}
                    onChange={(_e, keuze) => {
                      setCrewKiezer(false);
                      if (keuze?.id) bewaar({ crew: [...(d.crewIds || []), keuze.id] });
                    }}
                    onBlur={() => setCrewKiezer(false)}
                    renderInput={(params) => (
                      <TextField {...params} autoFocus variant="standard" placeholder="Zoek persoon…"
                        inputProps={{ ...params.inputProps, style: { fontSize: '0.8rem' } }} />
                    )} />
                )}
              </Stack>
              <AgendaOmschrijving tekst={d.omschrijving} />
            </Sectie>

            <Sectie titel="✅ Voorbereiding">
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              {checkbox('Preproductie voorbereid', 'preproductieVoorbereid')}
              {checkbox('Geen montage nodig', 'geenMontageNodig')}
              {checkbox('Geen preproductie nodig', 'geenPreproductieNodig')}
              <Tooltip arrow title={
                <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                  De voorbereiding gaat in twee stappen:<br />
                  1️⃣ De <b>PL</b> vult alles in en vinkt onderaan "alles ingevuld" → status oranje.<br />
                  2️⃣ De <b>producer</b> regelt alles en vinkt "Preproductie voorbereid" → status groen.<br />
                  "Geen preproductie nodig" slaat beide stappen over.
                </Box>
              }>
                <Box component="span" sx={{
                  width: 18, height: 18, borderRadius: '50%', border: '1px solid #cbd5e1',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, color: '#64748b', cursor: 'help',
                }}>?</Box>
              </Tooltip>
            </Stack>            </Sectie>

            <Sectie titel="📝 Quitclaims">
              <Stack spacing={1.25}>
                {selectVeld(d.quitclaimsNodig, 'quitclaimsNodig', QUITCLAIM_OPTIES, { placeholder: 'Quitclaims nodig?' })}
                {d.quitclaimsNodig && d.quitclaimsNodig !== 'Nee' && (
                  <>
                    {tekstVeld('quitclaimsHoeveel', { label: 'Hoeveel', rows: 1, type: 'number' })}
                    {selectVeld(d.quitclaimsVergoeding, 'quitclaimsVergoeding', ['Ja', 'Nee'], { placeholder: 'Vergoeding?' })}
                    {selectVeld(d.quitclaimsBuyout, 'quitclaimsBuyout', ['Bepaald', 'Onbepaald'], { placeholder: 'Buyout?' })}
                  </>
                )}
              </Stack>
            </Sectie>

            <Sectie titel="📞 Callsheet nodig?">
              {selectVeld(d.callsheetNodig, 'callsheetNodig', ['Ja', 'Opzet gemaakt', 'Nee'], { placeholder: 'Nog niet gekozen' })}
              {!d.callsheetId && d.callsheetNodig !== 'Nee' && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5, alignItems: 'center' }}
                  action={(
                    <Button size="small" variant="outlined" color="inherit" disabled={appBusy}
                      onClick={appDePl} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {appBusy ? <CircularProgress size={14} /> : '📱 App de PL'}
                    </Button>
                  )}>
                  Nog geen callsheet voor deze draaidag.
                </Alert>
              )}
              {/* Meerdaagse records hebben één callsheet per dag — toon ze
                  allemaal mét datum (ICTrecht toonde altijd de eerste). */}
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                {(d.callsheets || []).length > 0 ? (
                  d.callsheets.map((cs) => (
                    <Chip key={cs.id} size="small" icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      label={`${cs.datum ? `${euDatum(cs.datum)} · ` : ''}${cs.titel}`}
                      onClick={() => navigate('/callsheets')} sx={{ cursor: 'pointer', fontWeight: 700, maxWidth: 280 }} />
                  ))
                ) : d.callsheetId && (
                  <Chip size="small" icon={<OpenInNewIcon sx={{ fontSize: 14 }} />} label="Callsheet openen"
                    onClick={() => navigate('/callsheets')} sx={{ cursor: 'pointer', fontWeight: 700 }} />
                )}
                {(d.callsheetPdfs || []).map((pdf) => (
                  <Chip key={pdf.url} size="small" icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />}
                    label={pdf.naam.replace(/\.pdf$/i, '')}
                    component="a" href={pdf.url} target="_blank" clickable
                    sx={{ fontWeight: 700, maxWidth: 260 }} />
                ))}
                {!(d.callsheetPdfs || []).length && d.callsheetPdfUrl && (
                  <Chip size="small" icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />} label="PDF"
                    component="a" href={d.callsheetPdfUrl} target="_blank" clickable sx={{ fontWeight: 700 }} />
                )}
              </Stack>
            </Sectie>

            <Sectie titel="📄 Script (voor de crew-app)">
              <ScriptSectie draaidagId={draaidagId} />
            </Sectie>

            <Sectie titel="📎 Waarvan zijn printjes nodig?">
              {tekstVeld('printjesNodig', { rows: 3 })}
            </Sectie>

            <Sectie titel="🎩 Props">
              {tekstVeld('props', { rows: 3 })}
            </Sectie>

            <Sectie titel="🥐 Catering">
              <Stack spacing={1.25}>
                {selectVeld(d.catering, 'catering', CATERING_OPTIES, { multiple: true, placeholder: 'Selecteer wat van toepassing is' })}
                {tekstVeld('cateringAantal', { label: 'Aantal personen', rows: 1, type: 'number' })}
              </Stack>
            </Sectie>

            <Sectie titel="⭐ Bijzonderheden">
              {tekstVeld('bijzonderheden', { rows: 3 })}
            </Sectie>

            <Sectie titel="PL, heb je alles ingevuld?">
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                Vink dit aan als alle informatie hierboven compleet is — de status wordt
                <b> oranje</b> en de producer weet dat er geregeld kan worden. Zodra de
                producer alles geregeld heeft, zet die "Preproductie voorbereid" aan
                (bovenin) en wordt de status <b>groen</b>.
              </Typography>
              {checkbox('Alles ingevuld — producer kan aan de slag', 'productieVooruitIngevuld')}
            </Sectie>

            {d.projectId && (
              <Sectie titel="➕ Kleine taak (komt bij het project)">
                {!taakForm ? (
                  <Button size="small" variant="outlined" onClick={openTaakForm} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Taak toevoegen
                  </Button>
                ) : (
                  <Stack spacing={1}>
                    <TextField size="small" autoFocus label="Taak" value={taakForm.naam}
                      onChange={(e) => setTaakForm((f) => ({ ...f, naam: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') bewaarTaak(); }} />
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" type="number" label="Uren" value={taakForm.uren} sx={{ width: 90 }}
                        onChange={(e) => setTaakForm((f) => ({ ...f, uren: e.target.value }))} />
                      <TextField size="small" type="date" label="Datum" InputLabelProps={{ shrink: true }}
                        value={taakForm.datum} sx={{ width: 170 }}
                        onChange={(e) => setTaakForm((f) => ({ ...f, datum: e.target.value }))} />
                      <Autocomplete size="small" sx={{ flex: 1, minWidth: 160 }}
                        options={medewerkers}
                        getOptionLabel={(o) => o?.naam || ''}
                        value={medewerkers.find((m) => m.id === taakForm.assigneeId)
                          || (taakForm.assigneeId ? { id: taakForm.assigneeId, naam: taakForm.assigneeNaam || 'PL' } : null)}
                        onChange={(_e, keuze) => setTaakForm((f) => ({ ...f, assigneeId: keuze?.id || '', assigneeNaam: keuze?.naam || '' }))}
                        renderInput={(params) => <TextField {...params} label="Voor wie (standaard PL)" />} />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" onClick={bewaarTaak}
                        disabled={taakForm.busy || !taakForm.naam.trim()}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {taakForm.busy ? 'Bezig…' : 'Toevoegen'}
                      </Button>
                      <Button size="small" onClick={() => setTaakForm(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
                    </Stack>
                  </Stack>
                )}
              </Sectie>
            )}
            {d.taken?.length > 0 && (
              <Sectie titel="🗓️ Taken">
                <Box sx={{ border: '1px solid #e8edf2', borderRadius: 1.5, overflow: 'hidden' }}>
                  {d.taken.map((t) => (
                    <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderBottom: '1px solid #f4f7fa', '&:last-child': { borderBottom: 0 } }}>
                      <Typography noWrap sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0 }}>{t.naam}</Typography>
                      {t.assignee && <Chip size="small" label={t.assignee} sx={{ height: 20, fontSize: 11, bgcolor: '#f1f5f9' }} />}
                      {t.prio && <Chip size="small" label={t.prio} sx={{ height: 20, fontSize: 11, bgcolor: '#fef3c7', color: '#92400e' }} />}
                    </Box>
                  ))}
                </Box>
              </Sectie>
            )}

            <Sectie titel="🌍 Buitenland">
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                Buitenlandshoots moeten worden doorgegeven aan de reisverzekering. Wordt het land
                niet automatisch herkend, zet dan dit vinkje.
              </Typography>
              {checkbox('Dit is een buitenlandshoot', 'isBuitenlandHandmatig')}
            </Sectie>

            {bezig && (
              <Tooltip title="Bezig met opslaan…">
                <CircularProgress size={18} sx={{ position: 'fixed', bottom: 18, right: 18 }} />
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
